import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import OceanBackground from '../components/OceanBackground';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // Load from localStorage or default to light
    const savedMode = localStorage.getItem('theme-mode');
    return (savedMode as ThemeMode) || 'light';
  });

  useEffect(() => {
    // Save to localStorage whenever mode changes
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                // Light mode colors
                primary: {
                  main: '#0176D3',
                  light: '#3B82F6',
                  dark: '#005FB2',
                },
                secondary: {
                  main: '#10B981',
                  light: '#34D399',
                  dark: '#059669',
                },
                error: {
                  main: '#EF4444',
                  light: '#F87171',
                  dark: '#DC2626',
                },
                warning: {
                  main: '#F59E0B',
                  light: '#FCD34D',
                  dark: '#D97706',
                },
                info: {
                  main: '#3B82F6',
                  light: '#60A5FA',
                  dark: '#1D4ED8',
                },
                success: {
                  main: '#10B981',
                  light: '#34D399',
                  dark: '#059669',
                },
                background: {
                  default: '#F8FAFC',
                  paper: '#FFFFFF',
                },
                text: {
                  primary: '#1E293B',
                  secondary: '#64748B',
                },
              }
            : {
                // Dark mode colors
                primary: {
                  main: '#3B82F6',
                  light: '#60A5FA',
                  dark: '#1D4ED8',
                },
                secondary: {
                  main: '#10B981',
                  light: '#34D399',
                  dark: '#059669',
                },
                error: {
                  main: '#EF4444',
                  light: '#F87171',
                  dark: '#DC2626',
                },
                warning: {
                  main: '#F59E0B',
                  light: '#FCD34D',
                  dark: '#D97706',
                },
                info: {
                  main: '#3B82F6',
                  light: '#60A5FA',
                  dark: '#1D4ED8',
                },
                success: {
                  main: '#10B981',
                  light: '#34D399',
                  dark: '#059669',
                },
                background: {
                  default: '#000a14',
                  paper: 'rgba(30, 41, 59, 0.85)',
                },
                text: {
                  primary: '#F1F5F9',
                  secondary: '#94A3B8',
                },
              }),
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 800,
          },
          h2: {
            fontWeight: 700,
          },
          h3: {
            fontWeight: 700,
          },
          h4: {
            fontWeight: 700,
          },
          h5: {
            fontWeight: 700,
          },
          h6: {
            fontWeight: 700,
          },
          button: {
            textTransform: 'none',
            fontWeight: 600,
          },
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                padding: '8px 16px',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              },
              contained: {
                background: mode === 'light' 
                  ? 'linear-gradient(135deg, #0176D3 0%, #005FB2 100%)'
                  : 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                '&:hover': {
                  background: mode === 'light'
                    ? 'linear-gradient(135deg, #005FB2 0%, #004080 100%)'
                    : 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                boxShadow: mode === 'light'
                  ? '0 4px 16px rgba(0,0,0,0.08)'
                  : '0 4px 16px rgba(0,0,0,0.4)',
                backgroundImage: 'none',
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 600,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    [mode]
  );

  const value = useMemo(
    () => ({
      mode,
      toggleTheme,
    }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {mode === 'dark' && <OceanBackground />}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {children}
        </div>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
